import { AgentKnowledgeSource } from "@redotech/cockroachdb-codegen/tables/agent-knowledge/agent-knowledge-source";
import {
  faqKnowledgeIdFormat,
  merchantKnowledgeIdFormat,
} from "@redotech/shopper-agent-knowledge-id";
import * as z from "zod";
import {
  faqKnowledgeEntrySchema,
  faqKnowledgeListItemSchema,
} from "./faq-knowledge-view";
import {
  merchantKnowledgeArticleWithUsageSchema,
  merchantKnowledgeListItemSchema,
} from "./merchant-knowledge-view";

/**
 * One entry of the managed knowledge library, tagged by corpus so a surface
 * can tell editable merchant knowledge from read-only FAQ knowledge.
 */
export const managedKnowledgeItemSchema = z.discriminatedUnion("type", [
  merchantKnowledgeArticleWithUsageSchema.extend({
    type: z.literal(AgentKnowledgeSource.MerchantKnowledge),
  }),
  faqKnowledgeEntrySchema.extend({ type: z.literal(AgentKnowledgeSource.Faq) }),
]);
export type ManagedKnowledgeItem = z.infer<typeof managedKnowledgeItemSchema>;

export const MANAGED_KNOWLEDGE_PREVIEW_LENGTH = 300;

/** The body a list row shows: capped, with an ellipsis when truncated. */
export function managedKnowledgeContentPreview(content: string): string {
  const trimmed = content.trim();
  return trimmed.length <= MANAGED_KNOWLEDGE_PREVIEW_LENGTH
    ? trimmed
    : `${trimmed.slice(0, MANAGED_KNOWLEDGE_PREVIEW_LENGTH).trimEnd()}...`;
}

/** The above with its body reduced to a preview no list has to truncate. */
export const managedKnowledgeListItemSchema = z.discriminatedUnion("type", [
  merchantKnowledgeListItemSchema.extend({
    type: z.literal(AgentKnowledgeSource.MerchantKnowledge),
    contentPreview: z.string(),
  }),
  faqKnowledgeListItemSchema.extend({
    type: z.literal(AgentKnowledgeSource.Faq),
    contentPreview: z.string(),
  }),
]);
export type ManagedKnowledgeListItem = z.infer<
  typeof managedKnowledgeListItemSchema
>;

/** Swaps the body for its preview; the discriminant survives narrowing. */
export function toManagedKnowledgeListItem(
  item: ManagedKnowledgeItem,
): ManagedKnowledgeListItem {
  const { content, ...withoutBody } = item;
  return {
    ...withoutBody,
    contentPreview: managedKnowledgeContentPreview(content),
  };
}

/**
 * How a surface points at one managed entry: the list item's `type` and `id`,
 * so a row converts to a ref without knowing which corpus it came from.
 */
export const managedKnowledgeRefSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(AgentKnowledgeSource.MerchantKnowledge),
    id: merchantKnowledgeIdFormat.schema,
  }),
  z.object({
    type: z.literal(AgentKnowledgeSource.Faq),
    id: faqKnowledgeIdFormat.schema,
  }),
]);
export type ManagedKnowledgeRef = z.infer<typeof managedKnowledgeRefSchema>;
